/**
 * auth.tsx
 * Google Identity Services auth context.
 * Uses @react-oauth/google (wraps the new GIS library).
 *
 * Strategy: use the GoogleLogin button / One Tap which returns an ID token
 * (credential) directly. We decode the JWT payload client-side only to extract
 * name/email for display — the server verifies the token's signature.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import {
  GoogleOAuthProvider,
  GoogleLogin,
  type CredentialResponse,
} from '@react-oauth/google'
import { setApiToken, setOnUnauthorized } from './api'
import type { AuthUser } from './types'

// ---------------------------------------------------------------------------
// JWT decode (client-side, display only — server verifies)
// ---------------------------------------------------------------------------

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const parts = token.split('.')
    if (parts.length < 2) return {}
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      '=',
    )
    return JSON.parse(atob(padded)) as Record<string, unknown>
  } catch {
    return {}
  }
}

function tokenToUser(idToken: string): AuthUser {
  const claims = decodeJwtPayload(idToken)
  return {
    idToken,
    email: String(claims['email'] ?? ''),
    name: String(claims['name'] ?? claims['email'] ?? 'Partner'),
    picture: claims['picture'] ? String(claims['picture']) : undefined,
  }
}

// ---------------------------------------------------------------------------
// Session persistence (sessionStorage — cleared on tab close)
// ---------------------------------------------------------------------------

const SESSION_KEY = 'n73aa_auth'

function loadSavedAuth(): AuthUser | null {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as AuthUser) : null
  } catch {
    return null
  }
}

function persistAuth(user: AuthUser): void {
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(user))
  } catch {
    /* ignore */
  }
}

function clearPersistedAuth(): void {
  try {
    sessionStorage.removeItem(SESSION_KEY)
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface AuthContextValue {
  user: AuthUser | null;
  /** Call with a GIS credential (id_token) to sign in. */
  signInWithCredential: (idToken: string) => void;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null)

// ---------------------------------------------------------------------------
// AuthProvider
// ---------------------------------------------------------------------------

function InnerAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const saved = loadSavedAuth()
    if (saved) setApiToken(saved.idToken)
    return saved
  })

  useEffect(() => {
    setApiToken(user?.idToken ?? null)
  }, [user])

  const signInWithCredential = useCallback((idToken: string) => {
    const authUser = tokenToUser(idToken)
    setUser(authUser)
    persistAuth(authUser)
  }, [])

  const signOut = useCallback(() => {
    setUser(null)
    clearPersistedAuth()
  }, [])

  // Wire api.ts → AuthProvider: when any API call returns 401, drop the
  // session immediately so RequireAuth redirects to Landing on next render.
  // Avoids the "stale-page after timeout, requires manual refresh" bug.
  useEffect(() => {
    setOnUnauthorized(() => signOut())
    return () => setOnUnauthorized(null)
  }, [signOut])

  return (
    <AuthContext.Provider value={{ user, signInWithCredential, signOut }}>
      {children}
    </AuthContext.Provider>
  )
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID as string
  return (
    <GoogleOAuthProvider clientId={clientId}>
      <InnerAuthProvider>{children}</InnerAuthProvider>
    </GoogleOAuthProvider>
  )
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

// ---------------------------------------------------------------------------
// GoogleSignInButton — re-exported for use in Landing page
// ---------------------------------------------------------------------------

interface GoogleSignInButtonProps {
  onSuccess: (idToken: string) => void;
  onError?: () => void;
}

export function GoogleSignInButton({
  onSuccess,
  onError,
}: GoogleSignInButtonProps) {
  function handleSuccess(response: CredentialResponse) {
    if (response.credential) {
      onSuccess(response.credential)
    }
  }

  return (
    <GoogleLogin
      onSuccess={handleSuccess}
      onError={onError}
      useOneTap
      text="signin_with"
      shape="rectangular"
      size="large"
      width="280"
    />
  )
}
